import React from 'react'

import { useAuthContext } from 'features/auth/context/AuthContext'
import { AttachedCardDisplay } from 'features/home/components/AttachedModuleCard/AttachedCardDisplay'
import { AttachedCardImage } from 'features/home/components/AttachedModuleCard/AttachedCardImage'
import { getDistance } from 'libs/location/getDistance'
import { useUserLocation, usePlace, useLocationMode } from 'libs/locationV2/location.store'
import { formatDates, getTimeStampInMillis } from 'libs/parsers/formatDates'
import { formatPrice, getDisplayedPrice } from 'libs/parsers/getDisplayedPrice'
import { useCategoryHomeLabelMapping, useCategoryIdMapping } from 'libs/subcategories'
import { usePacificFrancToEuroRate } from 'queries/settings/useSettings'
import { getComputedAccessibilityLabel } from 'shared/accessibility/helpers/getComputedAccessibilityLabel'
import { useGetCurrencyToDisplay } from 'shared/currency/useGetCurrencyToDisplay'
import { Offer } from 'shared/offer/types'
import { isCurrentBeneficiary } from 'shared/user/checkStatusType'

type Props = {
  offer: Offer
  shouldFixHeight?: boolean
  comingSoon?: string
}

export const AttachedOfferCard: React.FC<Props> = ({ offer, shouldFixHeight, comingSoon }) => {
  const userLocation = useUserLocation()
  const selectedPlace = usePlace()
  const selectedLocationMode = useLocationMode()
  const { offer: attachedOffer } = offer
  const { user } = useAuthContext()
  const mapping = useCategoryIdMapping()
  const categoryId = mapping[attachedOffer.subcategoryId]
  const labelMapping = useCategoryHomeLabelMapping()
  const currency = useGetCurrencyToDisplay()
  const { data: euroToPacificFrancRate } = usePacificFrancToEuroRate()
  const categoryName = labelMapping[attachedOffer.subcategoryId] ?? ''
  const details: string[] = []

  const date = attachedOffer.dates && formatDates(getTimeStampInMillis(attachedOffer.dates))
  const price = getDisplayedPrice(
    attachedOffer.prices,
    currency,
    euroToPacificFrancRate,
    formatPrice({
      isDuo: !!(attachedOffer.isDuo && isCurrentBeneficiary(user)),
    })
  )
  const distance = getDistance(
    offer._geoloc,
    { userLocation, selectedPlace, selectedLocationMode },
    attachedOffer.subcategoryId
  )
  const distanceLabel = distance ? `à ${distance}` : undefined

  if (date) details.push(date)
  if (price) details.push(price)
  if (!attachedOffer.name) return null

  const accessibilityLabel = getComputedAccessibilityLabel(
    attachedOffer.name,
    categoryName,
    date,
    price,
    distanceLabel
  )

  return (
    <AttachedCardDisplay
      title={attachedOffer.name}
      subtitle={categoryName}
      details={details}
      rightTagLabel={distanceLabel}
      accessibilityLabel={accessibilityLabel}
      bottomBannerText={comingSoon}
      LeftImageComponent={AttachedCardImage}
      shouldFixHeight={shouldFixHeight}
      leftImageProps={{ imageUrl: attachedOffer?.thumbUrl, categoryId }}
    />
  )
}
